import React, { useEffect, useState, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { Calendar, TrendingUp, Clock, DollarSign, Users, Activity } from 'lucide-react';
import contractService from '../../services/contractService';
import type {
  ContractAnalytics,
  ContractTrends,
  ContractPerformance,
  AnalyticsFilters,
} from '../../types/contracts';
import { CONTRACT_STATUSES } from '../../types/contracts';
import LoadingSpinner from '../LoadingSpinner';
import ContractErrorBoundary from './ContractErrorBoundary';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface ContractAnalyticsDashboardProps {
  projectId?: string;
  className?: string;
}

const ContractAnalyticsDashboard: React.FC<ContractAnalyticsDashboardProps> = ({
  projectId,
  className = '',
}) => {
  const [analytics, setAnalytics] = useState<ContractAnalytics | null>(null);
  const [trends, setTrends] = useState<ContractTrends | null>(null);
  const [performance, setPerformance] = useState<ContractPerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<AnalyticsFilters>({
    projectId,
    dateRange: '30',
    status: 'all',
    type: 'all',
    period: 'monthly',
  });

  const [activeTab, setActiveTab] = useState<'overview' | 'trends' | 'performance'>('overview');

  const loadAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [analyticsData, trendsData, performanceData] = await Promise.all([
        contractService.getAnalytics(filters),
        contractService.getTrends({
          projectId: filters.projectId,
          period: filters.period,
        }),
        contractService.getPerformance({ projectId: filters.projectId }),
      ]);

      setAnalytics(analyticsData);
      setTrends(trendsData);
      setPerformance(performanceData);
    } catch (err) {
      setError('Failed to load analytics data');
      console.error('Analytics loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  const handleFilterChange = (key: keyof AnalyticsFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className={`p-6 ${className}`}>
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 ${className}`}>
        <div className="text-center py-12">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={loadAnalyticsData}
            className="px-4 py-2 bg-brand-orange text-white rounded-lg hover:bg-brand-orange-dark"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analytics || !trends || !performance) {
    return (
      <div className={`p-6 ${className}`}>
        <div className="text-center py-12">
          <div className="text-gray-500">No data available</div>
        </div>
      </div>
    );
  }

  return (
    <ContractErrorBoundary>
      <div className={`p-6 ${className}`}>
        {/* Header with Filters */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Contract Analytics</h2>
              <p className="text-gray-600 mt-1">
                Comprehensive insights into your contract performance
                {projectId && ' for this project'}
              </p>
            </div>
            <div className="text-xs text-gray-500">
              Last updated:{' '}
              {analytics?.generatedAt ? new Date(analytics.generatedAt).toLocaleString() : 'Never'}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date Range</label>
              <select
                value={filters.dateRange}
                onChange={e => handleFilterChange('dateRange', e.target.value)}
                className="text-sm border border-gray-300 rounded px-3 py-1"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
                className="text-sm border border-gray-300 rounded px-3 py-1"
              >
                {CONTRACT_STATUSES.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
              <select
                value={filters.period}
                onChange={e => handleFilterChange('period', e.target.value)}
                className="text-sm border border-gray-300 rounded px-3 py-1"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: Activity },
              { id: 'trends', label: 'Trends', icon: TrendingUp },
              { id: 'performance', label: 'Performance', icon: Clock },
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                    isActive
                      ? 'border-brand-orange text-brand-orange'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && analytics && <OverviewTab analytics={analytics} />}
        {activeTab === 'trends' && trends && <TrendsTab trends={trends} />}
        {activeTab === 'performance' && performance && <PerformanceTab performance={performance} />}
      </div>
    </ContractErrorBoundary>
  );
};

// Overview Tab Component
const OverviewTab: React.FC<{ analytics: ContractAnalytics }> = ({ analytics }) => {
  if (!analytics) return null;

  const {
    overview,
    statusBreakdown = [],
    typeBreakdown = [],
    milestones,
    valueAnalysis,
  } = analytics;

  // Guard against missing data
  if (!overview || !valueAnalysis) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Analytics data is incomplete</p>
      </div>
    );
  }

  // Status breakdown chart data
  const statusChartData = {
    labels: statusBreakdown.map(item => item._id || 'Unknown'),
    datasets: [
      {
        data: statusBreakdown.map(item => item.count || 0),
        backgroundColor: [
          '#3B82F6', // blue
          '#EF4444', // red
          '#10B981', // green
          '#F59E0B', // yellow
          '#8B5CF6', // purple
          '#EC4899', // pink
          '#6B7280', // gray
          '#F97316', // orange
        ],
        borderWidth: 0,
      },
    ],
  };

  // Type breakdown chart data
  const typeChartData = {
    labels: typeBreakdown.map(item => item._id || 'Unknown'),
    datasets: [
      {
        label: 'Contract Count',
        data: typeBreakdown.map(item => item.count || 0),
        backgroundColor: '#F97316',
        borderColor: '#EA580C',
        borderWidth: 1,
      },
      {
        label: 'Total Value',
        data: typeBreakdown.map(item => item.totalValue || 0),
        backgroundColor: '#3B82F6',
        borderColor: '#1D4ED8',
        borderWidth: 1,
        yAxisID: 'y1',
      },
    ],
  };

  return (
    <div className="space-y-8">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Contracts"
          value={(overview.totalContracts || 0).toLocaleString()}
          icon={<Calendar className="w-8 h-8" />}
          color="blue"
        />
        <MetricCard
          title="Total Value"
          value={`$${(overview.totalValue || 0).toLocaleString()}`}
          icon={<DollarSign className="w-8 h-8" />}
          color="green"
        />
        <MetricCard
          title="Active Contracts"
          value={(overview.activeContracts || 0).toLocaleString()}
          icon={<Activity className="w-8 h-8" />}
          color="orange"
        />
        <MetricCard
          title="Completion Rate"
          value={`${(overview.completionRate || 0).toFixed(1)}%`}
          icon={<TrendingUp className="w-8 h-8" />}
          color="purple"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Status Breakdown */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Breakdown</h3>
          <div className="h-64">
            <Doughnut
              data={statusChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Contract Types */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Contract Types</h3>
          <div className="h-64">
            <Bar
              data={typeChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    type: 'linear' as const,
                    display: true,
                    position: 'left' as const,
                  },
                  y1: {
                    type: 'linear' as const,
                    display: true,
                    position: 'right' as const,
                    grid: {
                      drawOnChartArea: false,
                    },
                  },
                },
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Value Analysis */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Value Analysis</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              ${(valueAnalysis.completedValue || 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Completed Value</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              ${(valueAnalysis.activeValue || 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Active Value</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              ${(valueAnalysis.averageValue || 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Average Contract Value</div>
          </div>
        </div>
      </div>

      {/* Milestone Summary */}
      {milestones && (milestones.totalMilestones || 0) > 0 && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Milestone Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {milestones.totalMilestones || 0}
              </div>
              <div className="text-sm text-gray-600">Total Milestones</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {milestones.completedMilestones || 0}
              </div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {milestones.overdueMilestones || 0}
              </div>
              <div className="text-sm text-gray-600">Overdue</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {(milestones.completionRate || 0).toFixed(1)}%
              </div>
              <div className="text-sm text-gray-600">Completion Rate</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Trends Tab Component
const TrendsTab: React.FC<{ trends: ContractTrends }> = ({ trends }) => {
  if (!trends || !trends.trends || trends.trends.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No trend data available</p>
      </div>
    );
  }

  // Contracts over time chart
  const contractsTrendData = {
    labels: trends.trends.map(item => item.period || 'Unknown'),
    datasets: [
      {
        label: 'Contracts Created',
        data: trends.trends.map(item => item.contractsCreated || 0),
        borderColor: '#F97316',
        backgroundColor: 'rgba(249, 115, 22, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Contracts Completed',
        data: trends.trends.map(item => item.completedContracts || 0),
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.1,
      },
    ],
  };

  // Value over time chart
  const valueTrendData = {
    labels: trends.trends.map(item => item.period || 'Unknown'),
    datasets: [
      {
        label: 'Total Contract Value',
        data: trends.trends.map(item => item.totalValue || 0),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Average Contract Value',
        data: trends.trends.map(item => item.averageValue || 0),
        borderColor: '#8B5CF6',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.1,
      },
    ],
  };

  return (
    <div className="space-y-8">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">
            {trends.trends.reduce((sum, item) => sum + (item.contractsCreated || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">Total Contracts in Period</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-green-600">
            ${trends.trends.reduce((sum, item) => sum + (item.totalValue || 0), 0).toLocaleString()}
          </div>
          <div className="text-sm text-gray-600">Total Value in Period</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">
            {(
              trends.trends.reduce((sum, item) => sum + (item.completionRate || 0), 0) /
              trends.trends.length
            ).toFixed(1)}
            %
          </div>
          <div className="text-sm text-gray-600">Average Completion Rate</div>
        </div>
      </div>

      {/* Charts */}
      <div className="space-y-8">
        {/* Contract Volume Trends */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Contract Volume Trends ({trends.period})
          </h3>
          <div className="h-64">
            <Line
              data={contractsTrendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                  },
                },
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Value Trends */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Contract Value Trends ({trends.period})
          </h3>
          <div className="h-64">
            <Line
              data={valueTrendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                  },
                },
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Performance Tab Component
const PerformanceTab: React.FC<{ performance: ContractPerformance }> = ({ performance }) => {
  if (!performance) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No performance data available</p>
      </div>
    );
  }

  const formatDuration = (milliseconds: number) => {
    const days = Math.floor((milliseconds || 0) / (1000 * 60 * 60 * 24));
    return `${days} days`;
  };

  return (
    <div className="space-y-8">
      {/* Performance Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Contract Completion Rate"
          value={`${(performance.completion?.completionRate || 0).toFixed(1)}%`}
          icon={<TrendingUp className="w-8 h-8" />}
          color="green"
        />
        <MetricCard
          title="On-Time Completion"
          value={`${(performance.completion?.onTimeRate || 0).toFixed(1)}%`}
          icon={<Clock className="w-8 h-8" />}
          color="blue"
        />
        <MetricCard
          title="Approval Rate"
          value={`${(performance.approvals?.approvalRate || 0).toFixed(1)}%`}
          icon={<Users className="w-8 h-8" />}
          color="purple"
        />
        <MetricCard
          title="Milestone Completion"
          value={`${(performance.milestones?.completionRate || 0).toFixed(1)}%`}
          icon={<Activity className="w-8 h-8" />}
          color="orange"
        />
      </div>

      {/* Detailed Performance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Completion Performance */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Completion Performance</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Completion Rate:</span>
              <span className="font-semibold text-green-600">
                {(performance.completion?.completionRate || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">On-Time Rate:</span>
              <span className="font-semibold text-blue-600">
                {(performance.completion?.onTimeRate || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Completion Time:</span>
              <span className="font-semibold text-gray-900">
                {formatDuration(performance.completion?.averageCompletionTime || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Approval Performance */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Approval Performance</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Approval Rate:</span>
              <span className="font-semibold text-green-600">
                {(performance.approvals?.approvalRate || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Rejection Rate:</span>
              <span className="font-semibold text-red-600">
                {(performance.approvals?.rejectionRate || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Approval Time:</span>
              <span className="font-semibold text-gray-900">
                {formatDuration(performance.approvals?.averageApprovalTime || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Milestone Performance */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Milestone Performance</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Completion Rate:</span>
              <span className="font-semibold text-green-600">
                {(performance.milestones?.completionRate || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">On-Time Rate:</span>
              <span className="font-semibold text-blue-600">
                {(performance.milestones?.onTimeRate || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Overdue Rate:</span>
              <span className="font-semibold text-red-600">
                {(performance.milestones?.overdueRate || 0).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        {/* Timing Metrics */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Timing Metrics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Contract Duration:</span>
              <span className="font-semibold text-gray-900">
                {formatDuration(performance.timing?.averageContractDuration || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Time to Approval:</span>
              <span className="font-semibold text-gray-900">
                {formatDuration(performance.timing?.averageTimeToApproval || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Shortest Contract:</span>
              <span className="font-semibold text-green-600">
                {formatDuration(performance.timing?.shortestContract || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Longest Contract:</span>
              <span className="font-semibold text-red-600">
                {formatDuration(performance.timing?.longestContract || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Metric Card Component
interface MetricCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'orange' | 'purple' | 'red' | 'gray';
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-100',
    green: 'text-green-600 bg-green-100',
    orange: 'text-orange-600 bg-orange-100',
    purple: 'text-purple-600 bg-purple-100',
    red: 'text-red-600 bg-red-100',
    gray: 'text-gray-600 bg-gray-100',
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div className="ml-4">
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-sm text-gray-600">{title}</div>
        </div>
      </div>
    </div>
  );
};

export default ContractAnalyticsDashboard;
